import React, { useState, useEffect } from 'react';
import { useLevelAccess } from '../../context/LevelAccessContext';
import { useToast } from '../../context/ToastContext';
import {
  apiGetFlashcardTopics,
  apiStartFlashcardSession,
  apiCheckFlashcardAnswer,
  apiCompleteFlashcardSession,
} from '../../utils/api';
import { Card, Button, Badge, ProgressBar, AIThinking, SectionHeader, Spinner } from '../../components/shared/UI';

// Grammar questions need a different input label
function getCardLabel(q) {
  if (!q) return 'Translate this:';
  if (q.type === 'grammar') return `Grammar — ${q.questionType || 'exercise'}:`;
  return 'Translate to English:';
}

function getCardDisplay(q) {
  // The "prompt" field is the full question text for AI questions
  return q?.prompt || q?.german_word || '';
}

export default function FlashcardsPage() {
  const { isLevelUnlocked } = useLevelAccess();
  const { addToast } = useToast();

  const [topics, setTopics]               = useState([]);
  const [topicsLoading, setTopicsLoading] = useState(true);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [sessionId, setSessionId]         = useState(null);
  const [cards, setCards]                 = useState([]);   // AI-generated questions from startSession
  const [currentIdx, setCurrentIdx]       = useState(0);
  const [answer, setAnswer]               = useState('');
  const [feedback, setFeedback]           = useState(null);
  const [checkingAnswer, setCheckingAnswer] = useState(false);
  const [sessionResults, setSessionResults] = useState([]);
  const [sessionComplete, setSessionComplete] = useState(false);
  const [sessionSummary, setSessionSummary]   = useState(null);
  const [loadingTopic, setLoadingTopic]       = useState(false);
  const [sendingReport, setSendingReport]     = useState(false);

  useEffect(() => {
    apiGetFlashcardTopics()
      .then(({ topics: t }) => setTopics(t))
      .catch(() => addToast('Failed to load topics', 'error'))
      .finally(() => setTopicsLoading(false));
  }, []);

  const topicsByLevel = ['A1', 'A2', 'B1', 'B2'].reduce((acc, lvl) => {
    acc[lvl] = topics.filter(t => t.level === lvl);
    return acc;
  }, {});

  // Start a session — backend returns AI-generated questions immediately
  const selectTopic = async (topic) => {
    if (!isLevelUnlocked(topic.level)) return;
    setLoadingTopic(true);
    try {
      const sessionData = await apiStartFlashcardSession(topic.id);
      // sessionData.questions is the AI-generated array from the backend
      const aiQuestions = sessionData.questions || [];
      setCards(aiQuestions);
      setSessionId(sessionData.sessionId);
      setSelectedTopic({ ...topic, title: sessionData.topicTitle || topic.title });
      setCurrentIdx(0);
      setFeedback(null);
      setAnswer('');
      setSessionResults([]);
      setSessionComplete(false);
      setSessionSummary(null);
    } catch (err) {
      addToast(err.message || 'Failed to start flashcard session', 'error');
    } finally {
      setLoadingTopic(false);
    }
  };

  const submitAnswer = async () => {
    if (!answer.trim()) { addToast('Please type your answer', 'warning'); return; }
    setCheckingAnswer(true);
    const currentCard = cards[currentIdx];
    try {
      // Send { questionId, studentAnswer } — controller accepts both questionId and legacy flashcardId
      const result = await apiCheckFlashcardAnswer(sessionId, currentCard.id, answer);
      setFeedback(result);
      setSessionResults(prev => [...prev, {
        card:          currentCard,
        answer,
        correct:       result.correct,
        correctAnswer: result.correctAnswer,
        // Record metadata for completeSession
        questionId:    currentCard.id,
        type:          currentCard.type,
        questionType:  currentCard.questionType,
        grammarPoint:  currentCard.grammarPoint,
        prompt:        currentCard.prompt,
      }]);
    } catch (err) {
      addToast(err.message || 'Failed to check answer', 'error');
    } finally {
      setCheckingAnswer(false);
    }
  };

  const nextCard = () => {
    if (currentIdx + 1 >= cards.length) {
      finishSession();
    } else {
      setCurrentIdx(i => i + 1);
      setAnswer('');
      setFeedback(null);
    }
  };

  const finishSession = async () => {
    try {
      // Pass client-side results so backend can compute score without FK issues
      const summary = await apiCompleteFlashcardSession(sessionId, sessionResults);
      setSessionSummary(summary);
    } catch {
      const localPct = sessionResults.length > 0
        ? Math.round((sessionResults.filter(r => r.correct).length / sessionResults.length) * 100) : 0;
      setSessionSummary({ score: localPct, weakAreas: [] });
    }
    setSessionComplete(true);
  };

  const sendReport = async () => {
    setSendingReport(true);
    addToast('Session report sent to your teacher!', 'success');
    setSendingReport(false);
  };

  const resetSession = () => {
    setSelectedTopic(null);
    setSessionId(null);
    setCards([]);
    setFeedback(null);
    setAnswer('');
    setSessionResults([]);
    setSessionComplete(false);
    setSessionSummary(null);
  };

  const localScore = sessionResults.length > 0
    ? Math.round((sessionResults.filter(r => r.correct).length / sessionResults.length) * 100) : 0;
  const displayScore = sessionSummary?.score ?? localScore;

  // ── TOPIC SELECTOR ────────────────────────────────────────────
  if (!selectedTopic) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <SectionHeader title="Flashcard Practice" subtitle="Select a topic to begin — Claude AI will generate vocabulary and grammar exercises for your level" />
        {topicsLoading ? (
          <div className="flex justify-center py-16"><Spinner size="lg" className="text-brand-600" /></div>
        ) : (
          ['A1', 'A2', 'B1', 'B2'].map(level => (
            <div key={level} className="mb-6">
              <div className="flex items-center gap-3 mb-3">
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Level {level}</h3>
                {!isLevelUnlocked(level) && <Badge variant="locked">🔒 Locked</Badge>}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {topicsByLevel[level].length === 0 && (
                  <p className="text-xs text-gray-400 col-span-3">No topics yet for this level.</p>
                )}
                {topicsByLevel[level].map(topic => {
                  const unlocked = isLevelUnlocked(level);
                  return (
                    <div key={topic.id}
                      onClick={() => unlocked ? selectTopic(topic) : addToast(`Unlock Level ${level} to access this topic`, 'warning')}
                      className={`rounded-xl border p-4 transition-all
                        ${unlocked
                          ? 'border-gray-200 hover:border-brand-300 hover:shadow-md cursor-pointer bg-white hover:bg-surface'
                          : 'border-gray-100 bg-gray-50 opacity-60 cursor-not-allowed'}`}>
                      <div className="flex items-start justify-between mb-2">
                        <span className="text-2xl">{unlocked ? '🃏' : '🔒'}</span>
                        <Badge variant={unlocked ? 'info' : 'default'}>{topic.level}</Badge>
                      </div>
                      <h4 className="text-sm font-semibold text-gray-800">{topic.title}</h4>
                      <p className="text-xs text-gray-500 mt-1">{topic.card_count ?? 0} vocab cards</p>
                      {unlocked && (
                        <p className="text-xs text-purple-600 mt-1">🤖 AI generates 10 mixed questions</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
        {loadingTopic && (
          <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-8 flex flex-col items-center gap-4 shadow-2xl">
              <Spinner size="lg" className="text-brand-600" />
              <div className="text-center">
                <p className="text-sm font-medium text-gray-800">Claude AI is generating your questions…</p>
                <p className="text-xs text-gray-500 mt-1">Creating vocabulary + grammar exercises for your level</p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── SESSION COMPLETE ──────────────────────────────────────────
  if (sessionComplete) {
    const missed = sessionResults.filter(r => !r.correct);
    const grammarMissed = missed.filter(r => r.type === 'grammar');
    const vocabMissed   = missed.filter(r => r.type !== 'grammar');
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        <Card className="p-8 text-center">
          <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4"
            style={{ background: displayScore >= 70 ? '#dcfce7' : '#fee2e2' }}>
            <span className="text-4xl">{displayScore >= 70 ? '🎉' : '💪'}</span>
          </div>
          <h2 className="font-display text-3xl font-bold text-gray-900 mb-1">Session Complete!</h2>
          <p className="text-gray-500 mb-6">You finished <strong>{selectedTopic.title}</strong></p>

          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="p-4 bg-blue-50 rounded-xl">
              <p className="text-2xl font-bold text-brand-500">{displayScore}%</p>
              <p className="text-xs text-gray-600">Score</p>
            </div>
            <div className="p-4 bg-green-50 rounded-xl">
              <p className="text-2xl font-bold text-green-600">{sessionResults.filter(r => r.correct).length}</p>
              <p className="text-xs text-gray-600">Correct</p>
            </div>
            <div className="p-4 bg-red-50 rounded-xl">
              <p className="text-2xl font-bold text-red-500">{missed.length}</p>
              <p className="text-xs text-gray-600">Missed</p>
            </div>
          </div>

          {missed.length > 0 && (
            <div className="text-left mb-6 space-y-3">
              {vocabMissed.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Vocabulary to review</h3>
                  <div className="space-y-1.5">
                    {vocabMissed.map((r, i) => (
                      <div key={i} className="flex justify-between items-start p-2.5 bg-red-50 rounded-lg">
                        <span className="text-xs text-gray-700 flex-1 pr-2">{r.card?.prompt || r.card?.german_word}</span>
                        <span className="text-xs font-medium text-green-700 flex-shrink-0">{r.correctAnswer}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {grammarMissed.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Grammar to review</h3>
                  <div className="space-y-1.5">
                    {grammarMissed.map((r, i) => (
                      <div key={i} className="p-2.5 bg-purple-50 rounded-lg">
                        <p className="text-xs font-medium text-purple-700">{r.card?.grammarPoint || r.questionType}</p>
                        <p className="text-xs text-gray-600 mt-0.5">{r.card?.prompt}</p>
                        <p className="text-xs text-green-700 mt-0.5">✓ {r.correctAnswer}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="p-3 bg-purple-50 rounded-lg">
                <p className="text-xs text-purple-700 font-medium">🤖 AI Note: Focus on spaced repetition for missed items. Grammar points will be flagged in your weak areas report.</p>
              </div>
            </div>
          )}

          <div className="flex gap-3 justify-center flex-wrap">
            <Button variant="secondary" onClick={resetSession}>Choose Topic</Button>
            <Button variant="primary"   onClick={() => selectTopic(selectedTopic)}>Retry Topic</Button>
            <Button variant="gold"      loading={sendingReport} onClick={sendReport}>📤 Send to Teacher</Button>
          </div>
        </Card>
      </div>
    );
  }

  // ── ACTIVE SESSION ────────────────────────────────────────────
  const currentCard = cards[currentIdx];
  const isGrammar   = currentCard?.type === 'grammar';

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <button onClick={resetSession} className="text-xs text-gray-400 hover:text-gray-600 mb-1">← Change topic</button>
          <h1 className="text-lg font-semibold text-gray-900">{selectedTopic.title}</h1>
        </div>
        <div className="flex items-center gap-2">
          {isGrammar && (
            <Badge variant="ai" size="xs">🤖 Grammar</Badge>
          )}
          <Badge variant="info">{currentIdx + 1} / {cards.length}</Badge>
        </div>
      </div>

      <ProgressBar value={currentIdx} max={cards.length} label="Progress" className="mb-6" />

      <Card className="p-8 mb-4">
        <p className="text-xs text-gray-400 uppercase tracking-wide mb-2">
          {getCardLabel(currentCard)}
        </p>

        {/* Grammar point badge */}
        {isGrammar && currentCard?.grammarPoint && (
          <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-purple-50 border border-purple-200 rounded-full text-xs font-medium text-purple-700 mb-3">
            📚 {currentCard.grammarPoint}
          </div>
        )}

        {/* Main question */}
        <h2 className={`font-display font-bold text-de-black text-center py-6
          ${isGrammar ? 'text-2xl leading-relaxed' : 'text-4xl'}`}>
          {getCardDisplay(currentCard)}
        </h2>

        {/* Hint */}
        {currentCard?.hint && !feedback && (
          <p className="text-xs text-gray-400 text-center mb-2 italic">💡 Hint: {currentCard.hint}</p>
        )}

        {!feedback && (
          <div className="mt-4">
            <input type="text" value={answer} onChange={e => setAnswer(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !checkingAnswer && submitAnswer()}
              placeholder={isGrammar ? 'Type your answer in German…' : 'Type your English translation…'}
              className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-brand-400 focus:outline-none text-base transition-all"
              disabled={checkingAnswer} autoFocus />
            <Button variant="primary" size="lg" className="w-full mt-3" loading={checkingAnswer} onClick={submitAnswer}>
              {checkingAnswer ? 'Checking…' : 'Submit Answer'}
            </Button>
          </div>
        )}

        {checkingAnswer && <AIThinking message="Claude AI is evaluating your answer…" />}

        {feedback && !checkingAnswer && (
          <div className={`mt-4 p-4 rounded-xl animate-fade-in
            ${feedback.correct ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xl">{feedback.correct ? '✅' : '❌'}</span>
              <span className={`font-semibold text-sm ${feedback.correct ? 'text-green-700' : 'text-red-700'}`}>
                {feedback.correct ? 'Correct!' : feedback.partialCredit ? 'Close, but not quite' : 'Not quite'}
              </span>
            </div>
            <p className="text-sm text-gray-700 mb-2">{feedback.explanation}</p>
            {!feedback.correct && (
              <p className="text-sm text-gray-700">
                <strong>Correct answer:</strong> {feedback.correctAnswer}
              </p>
            )}
            {feedback.grammarRule && (
              <div className="mt-2 p-2 bg-purple-50 rounded-lg">
                <p className="text-xs text-purple-700"><strong>Grammar rule:</strong> {feedback.grammarRule}</p>
              </div>
            )}
            {feedback.exampleSentence && (
              <div className="mt-2 pt-2 border-t border-current/10">
                <p className="text-xs text-gray-500"><em>Example: {feedback.exampleSentence}</em></p>
              </div>
            )}
            {feedback.tip && (
              <p className="text-xs text-purple-600 mt-2 font-medium">💡 {feedback.tip}</p>
            )}
            <Button variant={feedback.correct ? 'success' : 'primary'} size="md" className="w-full mt-3" onClick={nextCard}>
              {currentIdx + 1 >= cards.length ? 'See Results' : 'Next Card →'}
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}
